import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';
import { OutOfCreditsModal } from './OutOfCreditsModal';
import type { Workspace } from '../types/database';

// A popup over the dashboard side of a Locked (Lapsed or Cancelled) workspace, with the
// page (children) inert behind it. Its data is kept but hidden by the database until it's
// paid for; the sidebar stays usable for settings, switching workspace and deleting it.
// Owners and admins can still use the plans page to pay.
export function LockScreen({ workspace, children }: { workspace: Workspace; children: ReactNode }) {
  const { user } = useAuthStore();
  const location = useLocation();

  const { data: members } = useQuery({
    queryKey: ['lockScreenMembers', workspace.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workspace_members')
        .select('user_id, role, profiles:user_id(first_name, last_name, email)')
        .eq('workspace_id', workspace.id)
        .overrideTypes<
          Array<{
            user_id: string;
            role: string;
            profiles: { first_name: string | null; last_name: string | null; email: string | null } | null;
          }>,
          { merge: false }
        >();
      if (error) throw error;
      return data;
    },
  });

  if (!members) return null;

  const role = members.find((m) => m.user_id === user?.id)?.role;
  const canPay = role === 'owner' || role === 'admin';
  if (canPay && location.pathname === '/plans') return children;

  const owner = members.find((m) => m.role === 'owner')?.profiles;
  const ownerName =
    [owner?.first_name, owner?.last_name].filter(Boolean).join(' ') || owner?.email || 'the workspace owner';

  const isLapsed = workspace.state === 'lapsed';
  const title = isLapsed ? 'Your Trial ended' : 'Your Subscription ended';
  const message = !canPay
    ? `Ask ${ownerName} to resubscribe to see this workspace's results again.`
    : isLapsed
      ? 'Upgrade to see your results. Everything collected during your Trial is kept.'
      : workspace.subscription_status === 'unpaid'
        ? "Your last payment failed, so your Subscription ended. Resubscribe to resume tracking; your results are kept."
        : 'Resubscribe to resume tracking. Your results are kept and come back as soon as you do.';

  const benefits = !canPay
    ? []
    : isLapsed
      ? [
          'See everything collected during your Trial',
          'Resume collecting AI search data',
          'Unlock higher prompt and team limits',
        ]
      : [
          'Get all your historical results back',
          'Resume daily AI search tracking',
          'Keep your trend lines unbroken',
        ];

  return (
    <>
      <div inert>{children}</div>
      <OutOfCreditsModal
        isOpen
        className="is-locked"
        title={title}
        description={message}
        benefits={benefits}
        ctaLabel={canPay ? (isLapsed ? 'Upgrade now' : 'Resubscribe') : null}
      />
    </>
  );
}
